import React from 'react';
import { useParams } from 'react-router-dom';
import { StyleSheet, View, Text } from './components/base_components';

import { BusyOverlay, useBusy } from './components/busy_component';
import Breadcrumb from './components/breadcrumb';
import ProgressSpinner from './components/progress_spinner';
import TextButton from './components/buttons/text_button';
import StandardScreen from './components/standard_screen';
import TitleActionLine from './components/title_action_line';
import StandardBoxList from './components/standard_box_list';
import BoxPropList from './components/box_prop_list';
import VolumeSelect from './components/volume_select';

import DeviceStore from './stores/device_store';
import { useOrg } from './stores/user_store';

import { dateFormat, relativeDate } from './tools/util';

const styles = StyleSheet.create({
  deviceScreen: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  dialog: {
    marginLeft: '2rem',
    marginBottom: '2rem',
    flexDirection: 'column',
  },
  empty: {
    margin: '2em',
    fontSize: '1.5em',
  },
  text: {
    marginTop: '1em',
    fontSize: '1.5em',
  },
  input: {
    alignSelf: 'stretch',
    maxWidth: '40rem',
    marginTop: '1rem',
  },
  button: {
    marginTop: '1rem',
    alignSelf: 'flex-start',
  },
  actionButton: {
    marginLeft: '1rem',
  },
});

export default function DeviceScreen() {
  const [is_busy, setBusy, clearBusy] = useBusy();
  const params = useParams();
  const org = useOrg();
  const device_id = parseInt(params.device_id ?? '0', 10) || 0;
  const device = DeviceStore.useDevice(device_id);
  const [add_volume, setAddVolume] = React.useState(0);
  const [selected_volumes, setSelectedVolumes] = React.useState<any[]>([]);
  const [updates, setUpdates] = React.useState<any>({});

  const enable_list =
    device?.volume_list?.filter?.(
      (v, i) => selected_volumes.includes(i) && v.is_disabled
    ) ?? [];
  const disable_list =
    device?.volume_list?.filter?.(
      (v, i) => selected_volumes.includes(i) && !v.is_disabled
    ) ?? [];

  React.useEffect(() => {
    DeviceStore.fetchDevice(device_id);
  }, [device_id]);

  function _onAddPress() {
    if (add_volume && setBusy()) {
      DeviceStore.addVolume({ device_id, volume_id: add_volume }, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to add volume, please try again.');
        }
      });
    }
  }
  function _onEnablePress() {
    if (enable_list?.length > 0 && setBusy()) {
      const opts = { device_id, volume_list: enable_list };
      DeviceStore.enableVolumeList(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to enable volume, please try again.');
        }
      });
    }
  }
  function _onDisablePress() {
    if (disable_list?.length > 0 && setBusy()) {
      const opts = { device_id, volume_list: disable_list };
      DeviceStore.disableVolumeList(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to disable volume, please try again.');
        }
      });
    }
  }
  function _onSave(complete) {
    if (setBusy()) {
      const opts: any = {
        org_id: org?.org_id,
        device_id,
      };
      if (updates.device_name) {
        opts.device_name = updates.device_name;
      }
      if (updates.is_disabled !== undefined) {
        opts.is_disabled = updates.is_disabled === 'Disabled';
      }
      if (updates.is_upload_disabled !== undefined) {
        opts.is_upload_disabled = updates.is_upload_disabled === 'Disabled';
      }
      if (updates.is_download_disabled !== undefined) {
        opts.is_download_disabled = updates.is_download_disabled === 'Disabled';
      }
      if (updates.upload_priority !== undefined) {
        opts.upload_priority = updates.upload_priority;
      }
      if (updates.user_id !== undefined) {
        opts.user_id = updates.user_id;
      }
      DeviceStore.updateDevice(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to update device.');
        } else {
          complete?.();
        }
      });
    }
  }
  function _onDeviceVolumeKeyPress() {
    const yes = window.confirm(
      'This potentially exposes the entire contents of this volume if the key is leaked. Are you sure you want to get the key for this volume?'
    );
    if (yes) {
      const opts = {
        org_id: org?.org_id,
        device_id,
        volume_id: device?.volume_list?.[selected_volumes?.[0]]?.volume_id,
      };
      console.log('opts:', opts);
      DeviceStore.getDeviceVolumeKey(opts, (err, key) => {
        if (err) {
          window.alert('Failed to get volume key.');
        } else {
          window.alert('Volume Key: ' + key);
        }
      });
    }
  }

  let volumes: any = null;
  if (!device) {
    volumes = <ProgressSpinner />;
  } else if (device.volume_list.length === 0) {
    volumes = <Text style={styles.empty}>No Volumes</Text>;
  }

  const buttons = (
    <>
      {org?.is_admin ? (
        <TextButton
          style={styles.actionButton}
          text='Get Volume Key'
          disabled={selected_volumes.length !== 1}
          onPress={_onDeviceVolumeKeyPress}
        />
      ) : null}
      <TextButton
        style={styles.actionButton}
        text='Enable'
        disabled={enable_list.length === 0}
        onPress={_onEnablePress}
      />
      <TextButton
        style={styles.actionButton}
        text='Disable'
        disabled={disable_list.length === 0}
        onPress={_onDisablePress}
      />
    </>
  );

  return (
    <View style={styles.deviceScreen}>
      <StandardScreen>
        <Breadcrumb
          list={[{ text: 'Devices', url: '/device' }, device?.device_name]}
        />
        <TitleActionLine title={device?.device_name} />
        <BoxPropList
          title='Device Details'
          editable={org?.is_admin}
          updates={updates}
          onChange={setUpdates}
          onSave={_onSave}
          columns={[
            [
              {
                key: 'device_name',
                label: 'Device Name',
                value: device?.device_name,
                input: {
                  placeholder: 'Device Name',
                },
              },
              {
                key: 'user_id',
                label: 'User',
                value: device?.user_name,
                initialValue: device?.user_id,
                userSelect: {
                  orgId: org.org_id,
                },
              },
              {
                key: 'is_disabled',
                label: 'Status',
                value: device?.is_disabled ? 'Disabled' : 'Enabled',
                radio: {
                  options: ['Enabled', 'Disabled'],
                },
              },
              {
                label: 'Last Seen',
                value: dateFormat(device?.last_seen) || '-',
              },
            ],
            [
              {
                key: 'is_download_disabled',
                label: 'Download Status',
                value: device?.is_download_disabled ? 'Disabled' : 'Enabled',
                radio: {
                  options: ['Enabled', 'Disabled'],
                },
              },
              {
                key: 'is_upload_disabled',
                label: 'Upload Status',
                value: device?.is_upload_disabled ? 'Disabled' : 'Enabled',
                radio: {
                  options: ['Enabled', 'Disabled'],
                },
              },
              {
                key: 'upload_priority',
                label: 'Upload Priority',
                value: String(device?.upload_priority ?? 0),
                input: {
                  type: 'number',
                  min: 0,
                  max: 1000,
                },
              },
            ],
          ]}
        />
        <StandardBoxList
          title='Volumes'
          detail={device?.volume_list?.length}
          headers={['Volume Name', 'Status', 'Last Seen']}
          buttons={buttons}
          selected={selected_volumes}
          onChange={setSelectedVolumes}
          rows={device?.volume_list?.map?.((volume: any) => ({
            list: [
              volume.volume_name,
              volume.is_disabled ? 'Disabled' : 'Enabled',
              relativeDate(volume.last_seen) || '-',
            ],
          }))}
        >
          {volumes}
        </StandardBoxList>
        <StandardBoxList title='Add Volume'>
          <View style={styles.dialog}>
            <Text style={styles.text}>Add a volume to this device.</Text>
            <VolumeSelect
              style={styles.input}
              placeholder='Pick Volume'
              onlyOrgId={org.org_id}
              excludeList={device?.volume_list}
              value={add_volume}
              onChange={setAddVolume}
            />
            <TextButton
              style={styles.button}
              text='Add Volume'
              onPress={_onAddPress}
            />
          </View>
        </StandardBoxList>
      </StandardScreen>
      <BusyOverlay isBusy={is_busy} />
    </View>
  );
}
